import { Component, OnDestroy, OnInit } from '@angular/core';
import { DateTime } from 'luxon';
import { Task } from '../../domain/task';
import { ActivatedRoute } from '@angular/router';
import { PropertyStoreService } from '../../services/stores/property-store.service';
import { Property } from '../../domain/property';
import { TaskListType } from '../../domain/enums/task-list-type';
import { WorkflowTasksStoreService } from '../../services/stores/workflow-tasks-store.service';
import { LoadingOverlayStoreService } from '../../services/stores/loading-overlay-store.service';
import { DataStoreService } from '../../services/data-store.service';
import { RefreshDataState } from '../../domain/refresh-data-state';
import { PermissionsStoreService } from '../../services/stores/permission-store-service';
import { UserPermissions } from '../../domain/security/user-permissions';
import { WorkflowType } from '../../domain/enums/workflow-type';
import { DropDownStoreService } from '../../services/stores/drop-down-store.service';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
   selector: 'app-daily-task-list',
   templateUrl: './daily-task-list.component.html',
   styleUrls: ['./daily-task-list.component.scss']
})
export class DailyTaskListComponent implements OnInit, OnDestroy {
   isLoading: boolean;
   tasks: Task[];
   selectedDate: DateTime;
   userPermissions: UserPermissions;
   previousUrl: string | null = null;
   private currentProperty: Property | undefined;
   private listType: TaskListType;
   private readonly refreshDataStore: RefreshDataState;

   private destroy$ = new Subject<void>();

   private tasksSub?: Subscription;

   constructor(
      private route: ActivatedRoute,
      private propStore: PropertyStoreService,
      private workFlowTasksStore: WorkflowTasksStoreService,
      private loadingOverlayStore: LoadingOverlayStoreService,
      private dataStoreService: DataStoreService,
      private permissionsStore: PermissionsStoreService,
      private dropDownStore: DropDownStoreService
   ) {
      this.selectedDate = DateTime.now();
      this.userPermissions = new UserPermissions();
      this.refreshDataStore = new RefreshDataState();
      this.listType = TaskListType.AssignedTasks;
      this.tasks = [];
      this.isLoading = true;
   }

   ngOnInit(): Promise<void> {
      this.updateDropDownState(true);

      // Permissions (store)
      this.permissionsStore
         .get()
         .pipe(takeUntil(this.destroy$))
         .subscribe((p) => {
            this.userPermissions = p ?? new UserPermissions();
            this.getTasks(); // re-apply filtering once perms arrive
         });

      // Property
      this.propStore.currentProperty
         .pipe(takeUntil(this.destroy$))
         .subscribe((value) => {
            this.currentProperty = value;
            this.getTasks();
         });

      // Loading overlay
      this.loadingOverlayStore.LoadingState.pipe(takeUntil(this.destroy$)).subscribe(
         (loadingState) => {
            this.isLoading = !(
               !loadingState.AllPropertyTasks &&
               !loadingState.MyTasks &&
               !loadingState.MyAssigned
            );
         }
      );

      // Query params
      this.route.queryParams
         .pipe(takeUntil(this.destroy$))
         .subscribe((params: any) => {
            if (this.previousUrl === null && params?.from) {
               this.previousUrl = params.from;
               console.log('Captured previousUrl:', this.previousUrl);
            }

            if (params) {
               localStorage.setItem('tasklist_page', JSON.stringify(params));
            }

            // IMPORTANT: reset flags each time or they “stick”
            this.refreshDataStore.AllPropertyTasks = false;
            this.refreshDataStore.MyTasks = false;

            // listType
            if (params?.listType) {
               switch (+params.listType) {
                  case TaskListType.AllTasks:
                     this.listType = TaskListType.AllTasks;
                     this.refreshDataStore.AllPropertyTasks = true;
                     break;

                  case TaskListType.AssignedTasks:
                  default:
                     this.listType = TaskListType.AssignedTasks;
                     this.refreshDataStore.MyTasks = true;
                     break;
               }
            } else {
               // default behaviour (matches your constructor default)
               this.listType = TaskListType.AssignedTasks;
               this.refreshDataStore.MyTasks = true;
            }

            // date
            if (params?.selecteddate) {
               this.selectedDate = DateTime.fromISO(params.selecteddate);
               console.log('Parsed selectedDate:', this.selectedDate);

               if (this.selectedDate.isValid) {
                  this.workFlowTasksStore.setMyTasksDate(
                     this.selectedDate.toJSDate()
                  );
                  this.dataStoreService.refreshTaskData(this.refreshDataStore);
               } else {
                  console.error(this.selectedDate);
               }
            }

            this.getTasks();
         });

      return Promise.resolve();
   }

   ngOnDestroy(): void {
      this.tasksSub?.unsubscribe();

      this.destroy$.next();
      this.destroy$.complete();
   }

   // Method to update boolean inside DropDownStoreService
   updateDropDownState(value: boolean): void {
      this.dropDownStore.setDropDownState(value);
   }

   updatetaskListParamsInLocalStorage() {
      const year = this.selectedDate.year;
      const month = this.selectedDate.month;
      const day = this.selectedDate.day;

      const concatStr = `${year}-${month}-${day}`;

      const newParams = {
         selecteddate: concatStr,
         listType: '3'
      };

      localStorage.setItem('tasklist_page', JSON.stringify(newParams));
   }

   backButton(): void {
      if (this.previousUrl) {
         window.location.href = this.previousUrl;
      } else {
         window.location.href = '/dashboard';
      }
   }

   changeDate($event: number) {
      if ($event > 0) {
         this.selectedDate = this.selectedDate.plus({ days: 1 });
         this.updatetaskListParamsInLocalStorage();
      } else {
         this.selectedDate = this.selectedDate.minus({ days: 1 });
         this.updatetaskListParamsInLocalStorage();
      }

      if (this.selectedDate.isValid) {
         this.workFlowTasksStore.setMyTasksDate(this.selectedDate.toJSDate());
         this.dataStoreService.refreshTaskData(this.refreshDataStore);
      }
   }

   private getTasks() {
      if (this.currentProperty == null) return;

      // prevents leaks / repeated updates
      this.tasksSub?.unsubscribe();

      switch (this.listType as TaskListType) {
         case TaskListType.AllTasks:
            this.tasksSub = this.workFlowTasksStore.allTasksBySelectedDate
               .pipe(takeUntil(this.destroy$))
               .subscribe((data) => {
                  this.tasks = this.filterTasks(data);
                  this.processData();
               });
            break;

         case TaskListType.AssignedTasks:
            this.tasksSub = this.workFlowTasksStore.myTasksBySelectedDate
               .pipe(takeUntil(this.destroy$))
               .subscribe((data) => {
                  this.tasks = this.filterTasks(data);
                  this.processData();
               });
            break;

         default:
            console.log('Unknown Type of list');
            break;
      }
   }

   private filterTasks(tasks: Task[]): Task[] {
      if (
         (this.listType === TaskListType.AllTasks &&
            !this.userPermissions.forms.admin) ||
         (this.listType === TaskListType.AssignedTasks &&
            !this.userPermissions.forms.read)
      ) {
         return tasks.filter((f) => f.type !== WorkflowType.Form);
      }
      return tasks;
   }

   private processData(): void {
      if (!this.tasks) return;

      for (let i = 0; i < this.tasks.length; i++) {
         const currentTask = this.tasks[i];
         if (i - 1 >= 0) currentTask.prevTask = this.tasks[i - 1];
         if (i + 1 < this.tasks.length) currentTask.nextTask = this.tasks[i + 1];
      }
   }
}
